import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';

export interface UserPreferences {
  navigationLayout: 'header' | 'sidebar';
  theme?: 'light' | 'dark';
  language?: string;
  timezone?: string;
}

interface PreferencesState {
  preferences: UserPreferences | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: PreferencesState = {
  preferences: null,
  isLoading: false,
  error: null,
};

// Async thunk to fetch user preferences from database
export const fetchUserPreferences = createAsyncThunk(
  'preferences/fetchUserPreferences',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: { token: string | null } };
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch('/api/user-management/preferences', {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          // User preferences not found, return default
          console.log('User preferences not found, using defaults');
          return {
            navigationLayout: 'sidebar' as const,
            theme: 'light' as const,
            language: 'en',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          };
        }
        if (response.status === 401) {
          // User not authenticated, return default
          console.log('User not authenticated, using default preferences');
          return {
            navigationLayout: 'sidebar' as const,
            theme: 'light' as const,
            language: 'en',
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
          };
        }
        return rejectWithValue('Failed to fetch user preferences');
      }

      const data = await response.json();
      console.log('Preferences API response:', data);
      return data.data || data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

// Async thunk to update user preferences in database
export const updateUserPreferences = createAsyncThunk(
  'preferences/updateUserPreferences',
  async (preferences: Partial<UserPreferences>, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { auth: { token: string | null } };
      const token = state.auth.token;

      if (!token) {
        return rejectWithValue('No authentication token');
      }

      const response = await fetch('/api/user-management/preferences', {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });

      if (!response.ok) {
        return rejectWithValue('Failed to update user preferences');
      }

      const data = await response.json();
      console.log('Update preferences API response:', data);
      return data.data || data;
    } catch (error) {
      return rejectWithValue('Network error occurred');
    }
  }
);

const preferencesSlice = createSlice({
  name: 'preferences',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    // Initialize preferences - no localStorage dependency
    initializePreferences: (state) => {
      // Set default preferences
      state.preferences = {
        navigationLayout: 'sidebar',
        theme: 'light',
        language: 'en',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };
    },
    // Update navigation layout locally (for immediate UI update)
    updateNavigationLayout: (state, action: PayloadAction<'header' | 'sidebar'>) => {
      if (state.preferences) {
        state.preferences.navigationLayout = action.payload;
        // No localStorage needed - state is managed by Redux
      }
    },
    // Clear preferences
    clearPreferences: (state) => {
      state.preferences = null;
      state.error = null;
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch preferences cases
      .addCase(fetchUserPreferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.preferences = action.payload;
        state.error = null;
        console.log('✅ Preferences loaded from database:', action.payload);
        
        // Preferences are managed by Redux - no localStorage needed
      })
      .addCase(fetchUserPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        
        // Use default preferences if database fetch fails
        state.preferences = {
          navigationLayout: 'sidebar',
          theme: 'light',
          language: 'en',
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
      })
      // Update preferences cases
      .addCase(updateUserPreferences.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserPreferences.fulfilled, (state, action) => {
        state.isLoading = false;
        state.preferences = { ...state.preferences, ...action.payload };
        state.error = null;
        
        console.log('✅ Preferences updated successfully in Redux:', action.payload);
        // Preferences are managed by Redux - no localStorage needed
      })
      .addCase(updateUserPreferences.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { 
  clearError, 
  setLoading, 
  initializePreferences, 
  updateNavigationLayout, 
  clearPreferences 
} = preferencesSlice.actions;
export default preferencesSlice.reducer;
